import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, X } from "lucide-react";
import { imgSrc } from "@/lib/api";
import { useFavourites } from "@/context/FavouritesContext";
import { useCollection } from "@/hooks/useData";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export const Shortlist = () => {
  const { ids, toggle, clear } = useFavourites();
  const all = useCollection("properties");
  const [open, setOpen] = useState(false);
  const saved = useMemo(() => (all || []).filter((p) => ids.includes(p.id)), [all, ids]);
  if (!saved.length) return null;
  const summary = `Shortlist (${saved.length}): ${saved.map((p) => p.title).join("; ")}`.slice(0, 600);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="shortlist-open-button"
        className="fixed bottom-24 right-6 z-40 lab inline-flex items-center gap-2 bg-gw-btn text-gw-btnfg px-4 py-3 shadow-lg transition-transform duration-200 hover:-translate-y-1">
        <Heart size={15} className="fill-current" /> Shortlist ({saved.length})
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="theme-realty w-full sm:max-w-lg overflow-y-auto rounded-none bg-gw-paper2 text-gw-ink border-gw-rule/20" data-testid="shortlist-sheet">
          <SheetHeader>
            <SheetTitle className="lux text-3xl font-normal text-gw-ink">Your shortlist</SheetTitle>
            <SheetDescription className="text-gw-ink/70">Send one enquiry about every property you've saved.</SheetDescription>
          </SheetHeader>
          <ul className="mt-6 border-t border-gw-rule/20">
            {saved.map((p, i) => (
              <li key={p.id} className="flex items-center gap-3 py-3 border-b border-gw-rule/20" data-testid={`shortlist-item-${i}`}>
                <div className="w-16 h-12 bg-gw-card overflow-hidden shrink-0">
                  {p.images?.[0] && <img src={imgSrc(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <Link to={`/realty/${p.id}`} onClick={() => setOpen(false)} className="min-w-0 flex-1">
                  <p className="lux text-lg truncate">{p.title}</p>
                  <p className="text-xs opacity-70 truncate">{[p.listing_type, p.price, p.location].filter(Boolean).join(" · ")}</p>
                </Link>
                <button type="button" aria-label="Remove" onClick={() => toggle(p.id)} data-testid={`shortlist-remove-${i}`} className="p-2 opacity-70 hover:opacity-100"><X size={16} /></button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={clear} data-testid="shortlist-clear-button" className="lab text-[11px] mt-3 opacity-60 hover:opacity-100">Clear shortlist</button>
          <div className="mt-8">
            <EnquiryForm light testPrefix="shortlist-enquiry" defaultCompany="Wolfe Realtors"
              reference={{ type: "shortlist", id: null, title: summary }}
              onSent={() => { clear(); setOpen(false); }} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
